/**
 * Guardrails (§13).
 *
 * Every threshold on this screen is wired to an automatic action, and the tile
 * says which one. That is the whole point of §13: "every app in this category
 * has watched the ratio drift while someone argued about whether to intervene."
 * The console's job is to make the state of each switch readable in a second,
 * not to be the thing that flips it.
 *
 * The sparklines are drawn from readings this console has taken since it was
 * unlocked. /admin/metrics answers with a point in time and no history, so
 * rather than invent a series, the line says exactly how many readings it is
 * made of and when the first one was.
 */
import { RATIO_PAUSE_THRESHOLD } from '@alap/shared';
import type { ApiError, GuardrailRow, MetricsResponse, RatioSnapshotRow } from '../api';
import { ErrorState, Skeletons, Sparkline, clusterCity, clusterName, formatClock } from './kit';

export interface Sample {
  t: number;
  v: number;
}
export type History = Record<string, Sample[]>;

export default function Guardrails({
  metrics,
  loading,
  error,
  history,
  onRefresh,
}: {
  metrics: MetricsResponse | null;
  loading: boolean;
  error: ApiError | null;
  history: History;
  onRefresh: () => void;
}) {
  if (loading && !metrics) {
    return (
      <>
        <Intro />
        <Skeletons count={3} height={150} />
      </>
    );
  }

  if (error && !metrics) {
    return (
      <>
        <Intro />
        <ErrorState
          title="The guardrails did not load"
          message={error.message}
          code={`${error.code}${error.status ? ` · HTTP ${error.status}` : ''}`}
          onRetry={onRefresh}
        />
      </>
    );
  }

  if (!metrics) return null;

  const ratioRails = metrics.guardrails.filter((g) => g.key.startsWith('ratio:'));
  const productRails = metrics.guardrails.filter((g) => !g.key.startsWith('ratio:'));
  const breached = metrics.guardrails.filter((g) => g.breached);
  const thin = metrics.guardrails.filter((g) => !g.breached && (g.value === null || g.sample < g.minSample));
  const ok = metrics.guardrails.length - breached.length - thin.length;

  const ratioById = new Map<string, RatioSnapshotRow>(metrics.ratios.map((r) => [r.clusterId, r]));

  return (
    <>
      <Intro />

      <div className="summary">
        <div className={`summary-cell ${breached.length > 0 ? 'is-alert' : 'is-calm'}`}>
          <span className="figure num">{breached.length}</span>
          <span className="sub">{breached.length === 1 ? 'guardrail breached' : 'guardrails breached'}</span>
        </div>
        <div className="summary-cell">
          <span className="figure num">{ok}</span>
          <span className="sub">inside their threshold</span>
        </div>
        <div className="summary-cell">
          <span className="figure num">{thin.length}</span>
          <span className="sub">too small a sample to mean anything</span>
        </div>
        <div className="summary-cell" style={{ gridColumn: 'span 2', justifyContent: 'center' }}>
          <span className="eyebrow">North star</span>
          <span style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.45 }}>{metrics.northStar}</span>
        </div>
      </div>

      {error && (
        <div className="notice is-warn" role="status" style={{ marginBottom: 'var(--s-md)' }}>
          Showing the last good reading from {formatClock(metrics.generatedAt)}. The most recent
          refresh failed: {error.message}
        </div>
      )}

      {breached.length > 0 && (
        <div className="notice is-bad" role="status" style={{ marginBottom: 'var(--s-lg)' }}>
          <b>
            {breached.length === 1
              ? 'One guardrail is over its line'
              : `${breached.length} guardrails are over their line`}
            :
          </b>{' '}
          {breached.map((g) => g.label).join(' · ')}. Each one has already flipped the switch named
          on its tile — this screen reports the state, it does not wait for a decision.
        </div>
      )}

      <h3 className="eyebrow" style={{ marginBottom: 'var(--s-sm)' }}>
        Gender ratio, per city cluster · pause line at {Math.round(RATIO_PAUSE_THRESHOLD * 100)}:
        {100 - Math.round(RATIO_PAUSE_THRESHOLD * 100)}
      </h3>
      {ratioRails.length === 0 ? (
        <div className="state" style={{ marginBottom: 'var(--s-xl)' }}>
          <h3>No clusters have members yet</h3>
          <p>
            The ratio guardrail has nothing to watch until people sign up. Phase 0 is South Kolkata
            (§14).
          </p>
        </div>
      ) : (
        <div className="tile-grid" style={{ marginBottom: 'var(--s-xl)' }}>
          {ratioRails.map((g) => (
            <RatioTile
              key={g.key}
              rail={g}
              snapshot={ratioById.get(g.key.slice('ratio:'.length))}
              series={history[g.key] ?? []}
            />
          ))}
        </div>
      )}

      <h3 className="eyebrow" style={{ marginBottom: 'var(--s-sm)' }}>
        Product guardrails
      </h3>
      <div className="tile-grid">
        {productRails.map((g) => (
          <Tile key={g.key} rail={g} series={history[g.key] ?? []} />
        ))}
      </div>

      <p className="eyebrow" style={{ marginTop: 'var(--s-lg)' }}>
        Read at {formatClock(metrics.generatedAt)} · every read of this screen writes an audit row
        (§07)
      </p>
    </>
  );
}

function Intro() {
  return (
    <div className="page-intro">
      <h2 className="eyebrow">Guardrails</h2>
      <p>
        Six thresholds from §13, each wired to an automatic action rather than a meeting. A tile
        turns red when its number is over the line and its switch has already flipped.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Formatting
 * ------------------------------------------------------------------ */

function formatValue(unit: string, value: number): { figure: string; suffix: string } {
  switch (unit) {
    case 'fraction_men': {
      const men = Math.round(value * 100);
      return { figure: `${men}:${100 - men}`, suffix: 'men : women+NB' };
    }
    case 'reports_per_1000':
      return { figure: value.toFixed(1), suffix: 'per 1,000 conversations' };
    case 'hours':
      return value < 1
        ? { figure: `${Math.round(value * 60)}`, suffix: 'minutes' }
        : { figure: value.toFixed(1), suffix: 'hours' };
    case 'fraction':
      return { figure: `${Math.round(value * 100)}%`, suffix: '' };
    default:
      return { figure: String(value), suffix: unit };
  }
}

function formatThreshold(unit: string, threshold: number): string {
  switch (unit) {
    case 'fraction_men':
      return `${Math.round(threshold * 100)}% men`;
    case 'reports_per_1000':
      return `${threshold} per 1,000`;
    case 'hours':
      return `${threshold} h`;
    case 'fraction':
      return `${Math.round(threshold * 100)}%`;
    default:
      return String(threshold);
  }
}

function tileClass(rail: GuardrailRow): string {
  if (rail.breached) return 'is-breached';
  if (rail.value === null || rail.sample < rail.minSample) return 'is-thin';
  return 'is-ok';
}

function SparkBlock({ rail, series }: { rail: GuardrailRow; series: Sample[] }) {
  const first = series[0];
  const values = series.map((s) => s.v);

  return (
    <>
      <Sparkline
        values={values.length > 0 ? values : rail.value === null ? [] : [rail.value]}
        threshold={rail.threshold}
        thresholdLabel={formatThreshold(rail.unit, rail.threshold)}
        label={`${rail.label}: ${series.length} reading${series.length === 1 ? '' : 's'} this session, against a threshold of ${formatThreshold(rail.unit, rail.threshold)}`}
      />
      <div className="spark-caption">
        <span>
          {series.length <= 1
            ? 'first reading — the line fills in as the console polls'
            : `${series.length} readings this session`}
        </span>
        {first && <span className="num">since {formatClock(new Date(first.t).toISOString())}</span>}
      </div>
    </>
  );
}

function ThresholdLine({ rail }: { rail: GuardrailRow }) {
  return (
    <div className="tile-threshold">
      Threshold <b>{rail.breachWhen === 'above' ? '>' : '<'} {formatThreshold(rail.unit, rail.threshold)}</b>
      {rail.breached ? ' · over the line' : ''}
    </div>
  );
}

function ActionLine({ rail }: { rail: GuardrailRow }) {
  return (
    <div className="tile-action">
      <span className="eyebrow">{rail.breached ? 'Action taken' : 'Automatic action if breached'}</span>
      {rail.automaticAction}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Tiles
 * ------------------------------------------------------------------ */

function Tile({ rail, series }: { rail: GuardrailRow; series: Sample[] }) {
  const formatted = rail.value === null ? null : formatValue(rail.unit, rail.value);

  return (
    <article className={`tile ${tileClass(rail)}`}>
      <h4 className="tile-label">{rail.label}</h4>

      <div className="tile-figure">
        <span className="value">{formatted ? formatted.figure : '—'}</span>
        {formatted?.suffix && <span className="unit">{formatted.suffix}</span>}
      </div>

      <ThresholdLine rail={rail} />
      <SparkBlock rail={rail} series={series} />

      <div className="tile-sample">
        {rail.note ?? `sample of ${rail.sample.toLocaleString('en-IN')}`}
      </div>

      <ActionLine rail={rail} />
    </article>
  );
}

function RatioTile({
  rail,
  snapshot,
  series,
}: {
  rail: GuardrailRow;
  snapshot: RatioSnapshotRow | undefined;
  series: Sample[];
}) {
  const clusterId = rail.key.slice('ratio:'.length);
  const men = snapshot?.men ?? 0;
  const womenNb = snapshot?.womenNb ?? 0;
  const total = men + womenNb;
  const ratio = rail.value ?? 0;
  const menPct = Math.round(ratio * 100);
  const pausePct = rail.threshold * 100;

  return (
    <article className={`tile ${tileClass(rail)}`}>
      <h4 className="tile-label">
        {clusterName(clusterId)}
        {clusterCity(clusterId) && clusterCity(clusterId) !== clusterName(clusterId) ? (
          <span style={{ color: 'var(--muted)', fontWeight: 400 }}> · {clusterCity(clusterId)}</span>
        ) : null}
      </h4>

      <div className="tile-figure">
        <span className="value">{total === 0 ? '—' : `${menPct}:${100 - menPct}`}</span>
        <span className="unit">men : women+NB</span>
      </div>

      <div>
        <div className={`ratiobar ${rail.breached ? 'is-breached' : ''}`} role="img"
          aria-label={`${menPct}% men, ${100 - menPct}% women and non-binary. Pause line at ${Math.round(pausePct)}%.`}>
          <span className="men" style={{ width: `${Math.min(100, menPct)}%` }} />
          {menPct > pausePct && (
            <span className="over" style={{ left: `${pausePct}%`, width: `${Math.min(100, menPct) - pausePct}%` }} />
          )}
          <span className="pause-line" style={{ left: `${pausePct}%` }} />
        </div>
        <div className="ratiobar-legend">
          <span className="num">{men.toLocaleString('en-IN')} men</span>
          <span>pause at {Math.round(pausePct)}%</span>
          <span className="num">{womenNb.toLocaleString('en-IN')} women+NB</span>
        </div>
      </div>

      <SparkBlock rail={rail} series={series} />

      <div className="tile-sample">
        {total === 0
          ? 'nobody in this cluster yet'
          : total < rail.minSample
            ? `${total} active members — under ${rail.minSample}, so the guardrail stays quiet on purpose`
            : `${total.toLocaleString('en-IN')} active members`}
        {snapshot?.paused ? ' · male intake paused' : ''}
      </div>

      <ActionLine rail={rail} />
    </article>
  );
}
